import React, {
  ChangeEvent,
  MouseEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import { TextField, TextFieldProps } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import classNames from 'classnames';

import { useDebounce } from '../../hooks';
import { Box } from '../../Box';
import { Icon } from '../../Icon';
import { CircularProgress } from '../../CircularProgress';
import { Tooltip } from '../../Tooltip';
import { Typography } from '../../Typography';
import { Label, Preloader, Badge } from '../common';
import { useAutocompleteReducer } from '../hooks';
import { Filter } from '../types';

import { ListBox } from './ListBox';
import useStyles from './MultipleAutocompleteInput.styles';
import {
  MultipleAutocompleteInputOption,
  MultipleAutocompleteInputProps,
} from './MultipleAutocompleteInput.types';

const DEBOUNCE_TIMEOUT = 200;

/**
 * Функция для получения отображаемых значений выбранных предзагруженных элементов.
 *
 * @param {MultipleAutocompleteInputOption[]} items - Массив доступных элементов.
 * @param {string[] | undefined} value - Массив идентификаторов выбранных элементов.
 * @returns {string} - Строка с отображаемыми значениями выбранных элементов, разделенная запятыми.
 */

const getSelectedLoadValues = (
  items: MultipleAutocompleteInputOption[],
  value: string[] | undefined,
): string =>
  items
    .filter((item) => value?.includes(item.value))
    .map((item) => `${item.prefix ? `${item.prefix}, ` : ''}${item.label}`)
    .join(', ') || '';

/**
 * Компонент для ввода нескольких файлов.
 *
 * @param {Object} props - Свойства компонента.
 * @param {string} props.name - Имя поля ввода.
 * @param {Array<string>} [props.value] - Текущее значение поля ввода (массив идентификаторов выбранных файлов).
 * @param {function} props.onChange - Функция обратного вызова для обновления значения поля ввода.
 * @param {string} [props.label] - Метка для поля ввода.
 * @param {boolean} [props.required=false] - Обязательное поле ввода.
 * @param {string} [props.tooltip] - Текст подсказки для метки поля ввода.
 * @param {string} [props.placeholder] - Текст плейсхолдера для поля ввода.
 * @param {Array<Object>} [props.externalItems] - Внешние элементы (файлы), которые могут быть выбраны.
 * @param {string} [props.type='load'] - Тип загрузки элементов: 'load' - динамическая загрузка, 'local' - локальные элементы.
 * @param {number} [props.minInputValueLength=3] - Минимальная длина ввода для начала поиска.
 * @param {boolean} [props.loading=false] - Флаг, указывающий на то, что идет загрузка.
 * @param {boolean} [props.disabled=false] - Флаг, указывающий на то, что поле ввода отключено.
 * @param {boolean} [props.error=false] - Флаг, указывающий на наличие ошибки.
 * @param {string} [props.helperText] - Вспомогательный текст для поля ввода.
 * @param {boolean} [props.clearable=true] - Флаг, указывающий на возможность очистки выбранного значения.
 * @param {boolean} [props.allowSelectAll=false] - Флаг, указывающий на возможность выбора всех элементов.
 * @param {string} [props.selectAllLabel='Выбрать всех'] - Текст метки для выбора всех элементов.
 * @param {function} [props.requestItems] - Функция для запроса элементов.
 * @param {function} [props.renderTags] - Функция для рендера тегов выбранных элементов.
 * @param {function} [props.renderOption] - Функция для рендера опций выбранных элементов.
 * @param {Object} [props.additionalFilters={}] - Дополнительные фильтры для запроса элементов.
 * @param {string} [props.testId] - Идентификатор для тестирования.
 * @param {string} [props.disabledTooltip] - Текст подсказки для отключенных элементов.
 * @returns {JSX.Element} - Элемент MultipleFileInput.
 */

export const MultipleAutocompleteInput = (
  props: MultipleAutocompleteInputProps,
) => {
  const {
    value: initialValue,
    onChange,
    onBlur,
    onLogAction,
    label,
    tooltip,
    name,
    placeholder = '',
    required = false,
    limit = 50,
    disabledTooltip = 'Недоступен для выбранного типа заявки',
    disabled = false,
    clearable = true,
    loading = false,
    error = false,
    helperText = '',
    minInputValueLength = 1,
    renderTags,
    testId = 'test-id-multiple-autocomplete-input',
    additionalFilters,
    withoutMargin = false,
    renderOption,
  } = props;

  const stringifiedValue = JSON.stringify(
    initialValue ? [...initialValue].sort() : undefined,
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const value = useMemo(() => initialValue, [stringifiedValue]);

  const { classes } = useStyles();

  const { items, status, hasMore, requestItems } =
    useAutocompleteReducer<MultipleAutocompleteInputOption>(
      props.type === 'load' ? props.loadItems : undefined,
    );

  const [inputValue, setInputValue] = useState('');
  const [selectedValues, setSelectedValues] = useState(
    getSelectedLoadValues(
      props.type === 'local' ? props.externalItems : items,
      value,
    ),
  );
  const [allSelectedItems, setAllSelectedItems] = useState<
    MultipleAutocompleteInputOption[]
  >(
    props.type === 'local'
      ? props.externalItems.filter((item) => value?.includes(item.value))
      : [],
  );
  const [isFirstRenderDone, setIsFirstRenderDone] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState<Filter>({
    limit,
    offset: 0,
    query: '',
  });
  const [open, setOpen] = useState(false);
  const debouncedValue = useDebounce(inputValue, DEBOUNCE_TIMEOUT);
  const inputValueForUse =
    inputValue.length < minInputValueLength ? inputValue : debouncedValue;
  const isThereSomeGroup = items.some((option) => option.group);
  const isItemsLoading = isLoading || status === 'loading';

  const autocompleteGetOptions = () => {
    if (props.type === 'local') return props.externalItems;

    if (!filter.offset && isItemsLoading) {
      return [];
    }

    return items;
  };

  const currentItems = autocompleteGetOptions();

  const autocompleteOptions = useMemo(() => currentItems, [currentItems]);

  const autocompleteGetInputValue = () => {
    if (loading || !isFirstRenderDone) {
      return 'Загрузка...';
    } else if (!open && !inputValue) {
      return selectedValues;
    }

    return inputValue;
  };

  const autocompleteOpenHandler = () => {
    onLogAction?.('Открыть выпадающий список');
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setSelectedValues(getSelectedLoadValues(currentItems, value));
  };

  const autocompleteCloseHandler = () => {
    close();
  };

  const autocompleteBlurHandler = () => {
    if (open) {
      close();
    }

    onBlur?.();
  };

  const autocompleteGetOptionLabel = (
    option: MultipleAutocompleteInputOption,
  ) => `${option.prefix ? `${option.prefix}, ` : ''}${option.label}`;

  const autocompleteGetOptionSelected = (
    option: MultipleAutocompleteInputOption,
    selected: MultipleAutocompleteInputOption,
  ) => option.value === selected.value;

  const autocompleteGetOptionDisabled = (
    option: MultipleAutocompleteInputOption,
  ) => option.disabled || false;

  const autocompleteFocusHandler = () => {
    setOpen(true);
  };

  const autocompleteChangeHandler = (
    event: React.SyntheticEvent,
    nextValue: MultipleAutocompleteInputOption[],
    reason: string,
  ) => {
    event.stopPropagation();

    if (props.type === 'local') {
      onChange(nextValue.map((item) => item.value));
      setAllSelectedItems(nextValue);
    } else {
      let valuesToSet: string[] = [];

      if (reason === 'selectOption') {
        valuesToSet = [
          ...(value || []),
          ...nextValue
            .filter((item) => !value?.includes(item.value))
            .map((item) => item.value),
        ];
        setAllSelectedItems((prev) => [
          ...prev,
          ...nextValue.filter(
            (item) =>
              !prev.find(({ value: itemValue }) => itemValue === item.value),
          ),
        ]);
      } else if (reason === 'removeOption') {
        const valuesToRemove = items
          .filter(
            (item) =>
              value?.includes(item.value) &&
              !nextValue.find(
                ({ value: itemValue }) => itemValue === item.value,
              ),
          )
          .map((item) => item.value);
        valuesToSet = [
          ...(value || []).filter((item) => !valuesToRemove.includes(item)),
        ];
        setAllSelectedItems((prev) =>
          prev.filter((item) => valuesToSet.includes(item.value)),
        );
      }

      onChange(valuesToSet);
    }
  };

  const autocompleteClearHandler = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    onChange([]);
    setAllSelectedItems([]);
  };

  const autocompleteInputChangeHandler = (
    event: React.SyntheticEvent,
    nextValue: string,
    reason: string,
  ) => {
    if (
      (reason === 'reset' ||
        reason === 'selectOption' ||
        reason === 'removeOption') &&
      (!event ||
        (event as ChangeEvent<HTMLInputElement>)?.target.closest(
          'div#scrollable',
        ))
    )
      return;

    setInputValue(nextValue);

    if (reason === 'clear') {
      setSelectedValues('');
    }

    if (reason === 'input' && props.type === 'load') {
      setIsLoading(true);
      setFilter({
        ...filter,
        offset: 0,
        query: nextValue,
      });
    }
  };

  const autocompleteSelectAllChangeHandler = () => {
    if (props.type === 'load') return;

    if (
      value &&
      currentItems.filter((item) => !item.disabled).length === value.length
    ) {
      onChange([]);
      setSelectedValues('');
    } else {
      const itemsToSelect = currentItems
        .filter((item) => !item.disabled)
        .sort((a, b) => (a.label > b.label ? 1 : -1));

      onChange(itemsToSelect.map((item) => item.value));
      setSelectedValues(
        itemsToSelect
          .map(
            (item) => `${item.prefix ? `${item.prefix}, ` : ''}${item.label}`,
          )
          .join(', '),
      );
    }
  };

  useEffect(() => {
    if (props.type === 'local') return;

    if (
      !open ||
      (inputValueForUse !== '' && inputValueForUse.length < minInputValueLength)
    ) {
      return;
    }

    const abortRequest = requestItems?.(
      {
        ...filter,
        ...additionalFilters,
        offset: 0,
        query: inputValueForUse,
      },
      'search',
    );
    setFilter({
      ...filter,
      offset: filter.limit,
    });
    setIsLoading(false);

    return () => {
      abortRequest?.();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inputValueForUse]);

  useEffect(() => {
    (async () => {
      if (value?.length && !open && props.type === 'load' && !selectedValues) {
        const itemsByIds = await props.loadItems?.({
          limit: value.length,
          offset: 0,
          query: '',
          ids: value,
          ...additionalFilters,
        });

        const sortedItems = itemsByIds?.sort((a, b) =>
          a.label > b.label ? 1 : -1,
        );

        if (sortedItems)
          setSelectedValues(getSelectedLoadValues(sortedItems, value));

        setAllSelectedItems(sortedItems || []);
      }

      setIsFirstRenderDone(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, selectedValues]);

  useEffect(() => {
    setSelectedValues(
      getSelectedLoadValues(
        props.type === 'local' ? props.externalItems : items,
        value,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const defaultRenderOption = (
    optionProps: React.HTMLAttributes<HTMLLIElement>,
    option: MultipleAutocompleteInputOption,
  ) => {
    const { badge, additionalText } = option;
    const isBottom = badge?.position === 'bottom';

    return (
      <li {...optionProps} key={option.value}>
        <div
          className={classes.listItem}
          onClick={(event) => option.disabled && event.stopPropagation()}
        >
          <Checkbox
            checked={
              !!value && value.some((item: string) => item === option.value)
            }
            color="primary"
          />
          {badge && (
            <Box mr="8" order={isBottom ? 1 : 0}>
              <Badge
                title={badge.title}
                description={badge.description}
                color={badge.color}
              />
            </Box>
          )}
          {option.disabled ? (
            <div className={classes.listItemLabel}>
              <Tooltip
                title={`${disabledTooltip}${
                  option.tooltipText ? ` ${option.tooltipText}` : ''
                }`}
              >
                <div>
                  {option.prefix ? `${option.prefix}, ` : ''}
                  {option.label}
                </div>
              </Tooltip>
            </div>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              gap="4"
              maxWidth="calc(100% - 42px)"
            >
              <div className={classes.defaultWordBreak}>
                {option.prefix ? `${option.prefix}, ` : ''}
                {option.label}
              </div>

              {additionalText && (
                <Typography variant="caption" color="text.light.tertirary">
                  {option.additionalText}
                </Typography>
              )}
            </Box>
          )}
        </div>
      </li>
    );
  };

  const customListboxProps = {
    dataLength: currentItems.length,
    next: () => {
      requestItems?.({ ...filter, ...additionalFilters }, 'get');
      setFilter({
        ...filter,
        offset: filter.offset + filter.limit,
      });
    },
    hasMore: props.type === 'load' ? hasMore : false,
    loading: isItemsLoading,
  };

  return (
    <Box
      className={classNames(
        classes.multipleAutocompleteInput,
        !withoutMargin && classes.defaultMargin,
      )}
    >
      {label && <Label label={label} required={required} tooltip={tooltip} />}
      <Autocomplete
        disableClearable
        data-testid={testId}
        open={open}
        onOpen={autocompleteOpenHandler}
        onClose={autocompleteCloseHandler}
        onFocus={autocompleteFocusHandler}
        getOptionLabel={autocompleteGetOptionLabel}
        onChange={autocompleteChangeHandler}
        onBlur={autocompleteBlurHandler}
        classes={{
          paper: classes.paper,
          option: classes.option,
          listbox: classes.listbox,
        }}
        disabled={disabled || loading}
        multiple
        disableCloseOnSelect
        clearOnEscape
        options={autocompleteOptions}
        noOptionsText="Ничего не найдено..."
        value={currentItems.filter((item) => value?.includes(item.value))}
        defaultValue={[]}
        inputValue={autocompleteGetInputValue()}
        onInputChange={autocompleteInputChangeHandler}
        loading={isItemsLoading}
        loadingText={
          <Box display="flex" justifyContent="center" p="16">
            <CircularProgress size={30} />
          </Box>
        }
        renderOption={
          renderOption
            ? (optionProps, option) => (
                <li {...optionProps} key={option.value}>
                  {renderOption(option, value)}
                </li>
              )
            : defaultRenderOption
        }
        {...(isThereSomeGroup
          ? {
              groupBy: ({ group }) => {
                if (group) {
                  return group;
                }

                return 'Заявки без группировки по коллекциям';
              },
            }
          : {})}
        renderGroup={(params) => (
          <>
            <Box px="16" pt="8" pb="4">
              <Typography
                component="span"
                variant="caption"
                color="text.light.tertirary"
              >
                {params.group}
              </Typography>
            </Box>
            <Box>{params.children}</Box>
          </>
        )}
        isOptionEqualToValue={autocompleteGetOptionSelected}
        getOptionDisabled={autocompleteGetOptionDisabled}
        slotProps={{
          listbox: {
            component: ListBox,
            ...customListboxProps,
          },
        }}
        renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => {
          return (
            <TextField
              {...params}
              name={name}
              classes={{ root: classes.input }}
              variant="outlined"
              autoComplete="off"
              fullWidth
              disabled={disabled || loading || !isFirstRenderDone}
              error={error}
              helperText={helperText}
              placeholder={placeholder || label}
              slotProps={{
                input: {
                  className: params?.InputProps?.className,
                  ref: params?.InputProps?.ref,

                  endAdornment:
                    loading || !isFirstRenderDone ? (
                      <Preloader />
                    ) : (
                      <Box display="flex" alignItems="center" mx="16" gap="4">
                        {clearable && !disabled && !!value?.length && (
                          <Box width={24} height={24}>
                            <Box
                              className={`${classes.clearIcon} multipleAutocompleteClearIcon`}
                              onClick={autocompleteClearHandler}
                            >
                              <Icon
                                name="cancel_circle"
                                color="stroke.secondary"
                                size="20"
                              />
                            </Box>
                          </Box>
                        )}

                        <Box>{params?.InputProps?.endAdornment}</Box>
                      </Box>
                    ),
                },
              }}
            />
          );
        }}
      />
      {props.type === 'local' && props.allowSelectAll && (
        <FormControlLabel
          control={
            <Checkbox
              disabled={disabled}
              color="primary"
              checked={
                !!value?.length &&
                currentItems.filter((item) => !item.disabled).length ===
                  value.length
              }
              onChange={autocompleteSelectAllChangeHandler}
            />
          }
          label={
            <span className={classes.checkboxLabel}>
              {props.selectAllLabel || 'Выбрать всех'}
            </span>
          }
        />
      )}
      {renderTags && !disabled && !!value?.length && (
        <Box mt="16">
          {renderTags(
            value,
            props.type === 'load' ? allSelectedItems : props.externalItems,
          )}
        </Box>
      )}
    </Box>
  );
};
